define(['lib/tentacle/config'], function (config) {

    var filterSeparator = {};

    /**
     * Parser object traverses HTML DOM tree and finds controls in it. If control is found it is initialized in code
     * and inserted into DOM. Controls can be declared in two ways (prefixes are configurable):
     * 1. By HTML tag: <c-some-control ... > ... </c-some-control>. In this case control element is replaced by element
     *    provided by control view and its inner HTML is passed to control view as option `controlHtml`.
     * 2. By HTML attribute: <div c-some-control ... > ... </div>. In this case control view is initialized with
     *    control element and all inner HTML is preserved.
     *
     * Parser is initialized with three arguments:
     * 1. Mapping "controlId -> ControlClass". When control element is found in DOM its ID is converted to camel case,
     *    e.g. c-some-control -> someControl.
     * 2. Mapping "filterId -> filterObject".
     * 3. Context object which contains all values needed to properly initialize controls.
     **/
    var Parser = function (controls, filters, context) {
        this.controls = controls;
        this.filters = filters;
        this.context = context;
    };

    _.extend(Parser.prototype, {

        reDashAlpha: /-([\da-z])/gi,
        ccCallback: function(all, letter) { return (letter + '').toUpperCase(); },

        /**
         * Returns true if `str` starts with `substr`.
         **/
        startsWith: function (str, substr) {
            return str.indexOf(substr) === 0;
        },

        /**
         * Returns true if `str` ends with `substr`.
         **/
        endsWith: function (str, substr) {
            return str.lastIndexOf(substr) === str.length - substr.length;
        },

        /**
        * Splits array into parts by `divider` element. For example:
        *   var divider = {};
        *   splitArray([1, 2, divider, 3, 4, divider, divider], divider) -> [[1, 2], [3, 4], []]
        **/
        splitArray: function (arr, divider) {
            var result = [], current = [];

            _.each(arr, function (item) {
                if (item === divider) {
                    result.push(current);
                    current = [];
                } else {
                    current.push(item);
                }
            });

            if (current.length) {
                result.push(current);
            }

            return result;
        },

        /**
         * Alias for `startsWith()` method.
         **/
        hasPrefix: function (str, substr) {
            return this.startsWith(str, substr);
        },

        /**
         * Returns copy of `str` with first occurance of `substr` removed.
         **/
        removePrefix: function (str, substr) {
            return str.replace(substr, '');
        },

        /**
         * Converts dashed-style-string to camelCaseString. If `prefix` is provided, removes `prefix` from input
         * string first.
         **/
        toCamelCase: function (str, prefix) {
            if (prefix) { str = this.removePrefix(str, prefix); }
            return str.replace(this.reDashAlpha, this.ccCallback);
        },

        /**
         * Parses control option HTML DOM Attribute node. Example:
         *      opt-some-option="someValue" -> { name: 'someOption', value: 'someValue' }
         * (see `resolveToken` to understand how option values are resolved)
         **/
        parseOption: function (node) {
            return {
                name: this.toCamelCase(node.nodeName, config.pfxOption),
                value: this.resolveToken(node.nodeValue)
            };
        },

        /**
         * Parses control binding HTML DOM Attribute node. Example:
         *      bind-some-binding=".model:attr | filter1 arg1 arg2 | filter2" ->
         *      { binder: 'someBinder', model: {...}, attribute: 'attr', filters: [function () {...}, ...] }
         * (see `resolveToken` to understand how values are resolved)
         **/
        parseBinding: function (node) {
            var tokens = this.tokenize(node.nodeValue),
                parts = this.splitArray(tokens, filterSeparator),
                modelAttr = parts.shift()[0],
                filters = parts.map(function (part) {
                    var filter = part.shift();
                    if (!this.filters.hasOwnProperty(filter)) {
                        throw new Error('Unknown filter: ' + filter);
                    }
                    return this.encloseFilterArgs(this.filters[filter], part);
                }, this);

            return {
                binder: this.toCamelCase(node.nodeName, config.pfxBinding),
                model: modelAttr.model,
                attribute: modelAttr.attribute,
                filters: filters
            };
        },

        /**
         * Creates closure to enclose `args` passed to `filter`. If filter is two-way, encloses every function
         * in filter object.
         **/
        encloseFilterArgs: function (filter, args) {
            function enclose(foo) {
                return function (value) {
                    return foo.apply(this, [value].concat(args));
                };
            }

            if (_.isFunction(filter)) {
                return enclose(filter);
            } else {
                return { read: enclose(filter.read), publish: enclose(filter.publish) };
            }
        },

        /**
         * Splits string into tokens. Example:
         * ".model:attr | filter1 arg1 .arg2 | filter2 'a | b'" ->
         * ['.model:attr', {sep}, 'filter1', 'arg1', '.arg2', {sep}, 'filter2', 'a | b'] ->
         * [{ model: {...}, attribute: 'attr' }, {sep}, 'filter1', 'arg1', {...}, {sep}, 'filter2', 'a | b']
         *
         * Here {sep} is special filter separator object.
         * (see `resolveToken` to understand how tokens are resolved)
         **/
        tokenize: function (str) {
            var tokens = [], current = [], isInString = false, i, chr;

            function append(chr) {
                current.push(chr);
            }

            function flush() {
                if (current.length) {
                    tokens.push(current.join(''));
                    current = [];
                }
            }

            for (i = 0; i < str.length; ++i) {
                chr = str[i];

                if (chr === "'") {
                    if (isInString) {
                        append(chr);
                        flush();
                    } else {
                        flush();
                        append(chr);
                    }
                    isInString = !isInString;
                } else if (chr === " ") {
                    if (isInString) {
                        append(chr);
                    } else {
                        flush();
                    }
                } else if (chr === config.filterSeparator) {
                    if (isInString) {
                        append(chr);
                    } else {
                        flush();
                        tokens.push(filterSeparator);
                    }
                } else {
                    append(chr);
                }
            }

            flush();

            if (isInString) {
                throw new Error('Invalid attribute value: ' + str);
            }

            return tokens.map(function (token) { return this.resolveToken(token); }, this);
        },

        /**
         * Resolves string token using following rules:
         *  {sep} -> {sep} (special filter separator)
         *  ".obj" -> {Value by key 'obj' from context}
         *  ".model:attr" -> { model: {Object by key 'model' from context}, attribute: 'attr'}
         *  "true" -> true
         *  "false" -> false
         *  "null" -> null
         *  "undefined" -> undefined
         *  "1.234" -> 1.234
         *  "'some string'" -> 'some string'
         *  "other values" -> 'other values'
         */
        resolveToken: function (token) {
            if (token === filterSeparator) {
                return token;
            } else if (this.startsWith(token, '.')) {
                var arr = token.split(':'),
                    value = this.resolveContextValue(this.removePrefix(arr[0], '.'));
                if (arr[1]) {
                    if (!value) {
                        throw new Error('Unknown model: ' + token);
                    }
                    return { model: value, attribute: arr[1] };
                } else {
                    return value;
                }
            } else if (token === 'true') {
                return true;
            } else if (token === 'false') {
                return false;
            } else if (token === 'null') {
                return null;
            } else if (token === 'undefined') {
                return undefined;
            } else if (_.isFinite(token)) {
                return +token;
            } else if (this.startsWith(token, "'") && this.endsWith(token, "'")) {
                return token.substring(1, token.length - 1);
            } else {
                return token;
            }
        },

        /**
         * Returns value from context by dot-separated path. Example:
         *  Context: { a: { b: { c: 300 } } }; resolveContextValue('a.b.c') -> 300
         **/
        resolveContextValue: function (path) {
            return path.split('.').reduce(function (memo, part) { return memo[part]; }, this.context);
        },

        /**
         * Main parser function. Takes one or multiple HTML DOM elements for traversing.
         **/
        parse: function (els) {
            var controls = [];

            if (!_.isArray(els)) { els = [els]; }
            _.each(els, function (node) { this.processNode(node, controls); }, this);

            return controls;
        },

        /**
         * Function that processes HTML DOM `node`. If control is declared in node, calls `processControlNode` function
         * and appends new control to `controls` array. Then recursively traverses through `node` children.
         **/
        processNode: function (node, controls) {
            var names, id, isAttribute;

            if (node.nodeType !== 1) { return; }

            names = [node.nodeName].concat(_.map(node.attributes, function (attr) {
                return attr.nodeName;
            })).map(function(name) {
                return name.toLowerCase();
            });

            id = _.find(names, function (name) { return this.hasPrefix(name, config.pfxControl); }, this);
            isAttribute = (id !== names[0]);

            // Node is control. Process it.
            if (id) {
                var control = this.processControlNode(node, id, isAttribute);
                controls.push(control);
            }

            // If node is not control or control is declared as attribute, continue traversing.
            if (!id || isAttribute) {
                _.each(node.childNodes, function (childNode) {
                    this.processNode(childNode, controls);
                }, this);
            }
        },

        /**
         * Parses HTML DOM `node` attributes and returns object with following attributes:
         *  options - mapping of parsed control options (with "opt-" prefix)
         *  bindings - mapping of parsed control bindings (with "bind-" prefix)
         *  attributes - mapping of all other attributes (without "c-" prefix)
         **/
        processNodeAttributes: function (node) {
            var options = {}, bindings = [], attributes = {};

            _.each(node.attributes, function (attribute) {
                var name = attribute.nodeName.toLowerCase();

                if (this.hasPrefix(name, config.pfxOption)) {
                    var option = this.parseOption(attribute);
                    options[option.name] = option.value;
                } else if (this.hasPrefix(name, config.pfxBinding)) {
                    var binding = this.parseBinding(attribute);
                    bindings.push(binding);
                } else if (!this.hasPrefix(name, config.pfxControl)) {
                    attributes[name] = attribute.nodeValue;
                }
            }, this);

            return { options: options, bindings: bindings, attributes: attributes };
        },

        /**
         * Creates control in place of `node`. See Parser initialization to understand how controls can be declared
         * in DOM.
         **/
        processControlNode: function (node, id, isAttribute) {
            var idCamelCase = this.toCamelCase(id, config.pfxControl),
                Control, control, params;

            if (!this.controls.hasOwnProperty(idCamelCase)) {
                throw new Error('Unknown control: ' + id);
            }

            Control = this.controls[idCamelCase];
            params = this.processNodeAttributes(node);

            // Do some declaration-specific stuff
            if (isAttribute) {
                params.options.el = node;
            } else {
                params.options.controlHtml = node.innerHTML;
            }

            // Create control and bindings for it
            control = new Control(params.options).updateAttributes(params.attributes);
            _.each(params.bindings, function (binding) {
                if (!control.binders.hasOwnProperty(binding.binder)) {
                    throw new Error('Unknown binder for control {0}: {1}'.format(id, binding.binder));
                }

                control.bind(binding.binder, binding.model, binding.attribute, binding.filters);
            });

            // Again do some declaration-specific stuff
            if (isAttribute) {
                for (var i = node.attributes.length - 1; i >= 0; --i) {
                    node.removeAttributeNode(node.attributes[i]);
                }
                $(node).attr(control.attributes);
            } else {
                $(node).replaceWith(control.$el);
            }

            return control;
        }

    });

    return Parser;
});
